import {
	DEFAULT_TIME_SCALE_CONFIG,
	TICK_INTERVAL_MULTIPLIERS,
	TARGET_MAJOR_TICK_SPACING_PX,
	TARGET_MINOR_TICK_SPACING_PX,
	TIME_SCALES,
	FLOATING_POINT_TOLERANCE,
	TICK_RATIO_ADJUSTMENT
} from './constants';
import type { TimelineConfig, TimeScale } from '../types';

/**
 * Rounds a target value to the nearest readable interval.
 * Uses the sequence: 1, 2, 5, 10, 20, 50, 100, etc.
 *
 * @param target - The target interval value to round
 * @returns Rounded interval that is visually pleasant and easy to read
 */
function findReadableInterval(target: number): number {
	const magnitude = 10 ** Math.floor(Math.log10(target));
	const normalized = target / magnitude;
	const multiplier = TICK_INTERVAL_MULTIPLIERS.reduce((prev, curr) =>
		Math.abs(curr - normalized) < Math.abs(prev - normalized) ? curr : prev
	);

	return multiplier * magnitude;
}

/**
 * Selects the appropriate time scale based on video duration.
 */
function getTimeScaleForDuration(videoDuration: number) {
	return TIME_SCALES.find((scale) => videoDuration <= scale.threshold) || TIME_SCALES[TIME_SCALES.length - 1];
}

/**
 * Calculates the major tick interval based on video duration and available width.
 */
function calculateMajorInterval(videoDuration: number, usableWidth: number): number {
	const targetMajorTickCount = Math.max(
		TICK_RATIO_ADJUSTMENT.MIN_MAJOR_TICK_COUNT,
		Math.floor(usableWidth / TARGET_MAJOR_TICK_SPACING_PX)
	);
	const rawMajorInterval = videoDuration / targetMajorTickCount;
	const timeScale = getTimeScaleForDuration(videoDuration);

	const rawInBaseUnits = rawMajorInterval / timeScale.baseUnit;
	const readableInBaseUnits = findReadableInterval(rawInBaseUnits);

	return readableInBaseUnits * timeScale.baseUnit;
}

/**
 * Calculates the initial minor tick interval based on video duration and available width.
 */
function calculateMinorInterval(videoDuration: number, usableWidth: number): number {
	const targetMinorTickCount = Math.floor(usableWidth / TARGET_MINOR_TICK_SPACING_PX);
	const rawMinorInterval = videoDuration / targetMinorTickCount;
	const timeScale = getTimeScaleForDuration(videoDuration);

	const rawMinorInBaseUnits = rawMinorInterval / timeScale.baseUnit;
	const readableMinorInBaseUnits = findReadableInterval(rawMinorInBaseUnits);

	return readableMinorInBaseUnits * timeScale.baseUnit;
}

/**
 * Adjusts minor interval to maintain a good ratio with major interval.
 * Ensures minor ticks are appropriately spaced relative to major ticks.
 */
function adjustMinorIntervalRatio(minorInterval: number, majorInterval: number): number {
	if (minorInterval >= majorInterval) {
		return majorInterval / TICK_RATIO_ADJUSTMENT.FALLBACK_DIVISOR;
	}

	const ratio = majorInterval / minorInterval;
	const { RATIO_THRESHOLDS, DIVISORS } = TICK_RATIO_ADJUSTMENT;

	if (ratio < RATIO_THRESHOLDS.VERY_CLOSE) {
		return majorInterval / DIVISORS.FOR_VERY_CLOSE_RATIO;
	}
	if (ratio < RATIO_THRESHOLDS.CLOSE) {
		return majorInterval / DIVISORS.FOR_CLOSE_RATIO;
	}
	if (ratio < RATIO_THRESHOLDS.MODERATE) {
		return majorInterval / DIVISORS.FOR_MODERATE_RATIO;
	}

	return minorInterval;
}

/**
 * Calculates optimal time scale configuration for timeline rendering.
 *
 * @param videoDuration - Total duration of the video in seconds
 * @param config - Optional timeline configuration for tick heights and margins
 * @param availableWidth - Available width for the timeline in pixels (default: 1200)
 * @returns TimeScale object with major/minor intervals and tick heights
 *
 * @example
 * const scale = calculateScale(60, undefined, 1000);
 * // Returns: { majorInterval: 5, minorInterval: 1, majorHeight: 12, minorHeight: 8 }
 */
export function calculateScale(videoDuration: number, config?: TimelineConfig, availableWidth = 1200): TimeScale {
	const resolvedConfig = {
		majorHeight: config?.majorHeight ?? DEFAULT_TIME_SCALE_CONFIG.MAJOR_HEIGHT,
		minorHeight: config?.minorHeight ?? DEFAULT_TIME_SCALE_CONFIG.MINOR_HEIGHT,
		margin: config?.margin ?? DEFAULT_TIME_SCALE_CONFIG.MARGIN
	};

	const usableWidth = availableWidth - resolvedConfig.margin * 2;
	const majorInterval = calculateMajorInterval(videoDuration, usableWidth);
	const rawMinorInterval = calculateMinorInterval(videoDuration, usableWidth);
	const minorInterval = adjustMinorIntervalRatio(rawMinorInterval, majorInterval);

	return {
		majorInterval,
		minorInterval,
		majorHeight: resolvedConfig.majorHeight,
		minorHeight: resolvedConfig.minorHeight
	};
}

/**
 * Checks if two numbers are approximately equal within floating point tolerance.
 *
 * @param a - First number to compare
 * @param b - Second number to compare
 * @returns True if numbers are equal within tolerance
 */
function isApproximatelyEqual(a: number, b: number): boolean {
	return Math.abs(a - b) < FLOATING_POINT_TOLERANCE;
}

function isAtMajorTick(time: number, majorInterval: number): boolean {
	const remainder = time % majorInterval;
	return isApproximatelyEqual(remainder, 0) || isApproximatelyEqual(remainder, majorInterval);
}

/**
 * Generates major tick positions for timeline rendering.
 *
 * Creates an array of time values where major ticks should be placed.
 * Uses counter-based iteration to avoid floating point accumulation errors.
 * Always includes the video start (0) and end (videoDuration) positions.
 *
 * @param videoDuration - Total duration of the video in seconds
 * @param majorInterval - Interval between major ticks in seconds
 * @returns Array of time values in seconds where major ticks should be drawn
 *
 * @example
 * getMajorTicks(10, 2.5) // returns [0, 2.5, 5, 7.5, 10]
 */
export function getMajorTicks(videoDuration: number, majorInterval: number): number[] {
	const ticks: number[] = [];
	const tickCount = Math.floor(videoDuration / majorInterval) + 1;

	for (let i = 0; i < tickCount; i += 1) {
		const time = i * majorInterval;

		if (time > videoDuration + FLOATING_POINT_TOLERANCE) break;

		const isDuplicate = ticks.some((existingTick) => isApproximatelyEqual(existingTick, time));
		if (!isDuplicate) {
			ticks.push(time);
		}
	}

	const lastTick = ticks[ticks.length - 1];

	if (lastTick !== undefined && !isApproximatelyEqual(lastTick, videoDuration)) {
		ticks.push(videoDuration);
	}

	return ticks;
}

/**
 * Generates minor tick positions for timeline rendering.
 *
 * Creates an array of time values where minor ticks should be placed.
 * Excludes positions that coincide with major ticks to avoid duplicate rendering.
 * Uses counter-based iteration to avoid floating point accumulation errors.
 *
 * @param videoDuration - Total duration of the video in seconds
 * @param majorInterval - Interval between major ticks in seconds (used for collision detection)
 * @param minorInterval - Interval between minor ticks in seconds
 * @returns Array of time values in seconds where minor ticks should be drawn
 *
 * @example
 * // With major ticks at [0, 5, 10] and minor interval of 1
 * getMinorTicks(10, 5, 1) // returns [1, 2, 3, 4, 6, 7, 8, 9]
 */
export function getMinorTicks(videoDuration: number, majorInterval: number, minorInterval: number): number[] {
	const ticks: number[] = [];
	const tickCount = Math.floor(videoDuration / minorInterval) + 1;

	for (let i = 0; i < tickCount; i += 1) {
		const time = i * minorInterval;

		if (time > videoDuration) break;

		if (!isAtMajorTick(time, majorInterval)) {
			ticks.push(time);
		}
	}

	return ticks;
}
